import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { teacherInvoicesApi, teachersApi, uploadFile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileSpreadsheet, Plus, Trash2, ExternalLink, Calendar, Euro, CheckCircle, Clock, AlertCircle, Upload } from 'lucide-react';

export default function TeacherInvoicesPage() {
  const { user } = useAuth();
  // Admin / super_admin / RSPP gestiscono le fatture, gli altri (es. docente_online)
  // vedono solo le proprie fatture + acquisti Stripe.
  const isAdmin = ['super_admin', 'admin', 'amministratore', 'rspp'].includes(user?.role);
  const [invoices, setInvoices] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterTeacher, setFilterTeacher] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    teacher_id: '',
    invoice_number: '',
    amount: '',
    description: '',
    file: null,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const invoicesRes = await teacherInvoicesApi.getAll();
      setInvoices(invoicesRes.data);
      if (isAdmin) {
        try {
          const teachersRes = await teachersApi.getAll();
          setTeachers(teachersRes.data);
        } catch (e) { /* docenti non admin non leggono /teachers */ }
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      teacher_id: '',
      invoice_number: '',
      amount: '',
      description: '',
      file: null,
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      let file_url = null;
      if (formData.file) {
        const uploadRes = await uploadFile('invoices', formData.file);
        file_url = uploadRes.data.url;
      }
      
      await teacherInvoicesApi.create({
        teacher_id: formData.teacher_id,
        invoice_number: formData.invoice_number,
        amount: parseFloat(formData.amount),
        description: formData.description,
        file_url,
        issue_date: formData.issue_date,
        due_date: formData.due_date,
        notes: formData.notes
      });
      
      toast.success('Fattura caricata');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setUploading(false);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await teacherInvoicesApi.markPaid(id);
      toast.success('Fattura segnata come pagata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa fattura?')) return;
    try {
      await teacherInvoicesApi.delete(id);
      toast.success('Fattura eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher?.name || 'Sconosciuto';
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filterTeacher !== 'all' && inv.teacher_id !== filterTeacher) return false;
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    return true;
  });

  const totalPending = invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);

  const statusIcons = {
    pending: <Clock className="w-4 h-4 text-amber-500" />,
    paid: <CheckCircle className="w-4 h-4 text-green-500" />,
    overdue: <AlertCircle className="w-4 h-4 text-red-500" />
  };

  const statusLabels = {
    pending: 'In attesa',
    paid: 'Pagata',
    overdue: 'Scaduta'
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">{isAdmin ? 'Fatture Docenti' : 'Le mie Fatture e Acquisti'}</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin
              ? 'Gestisci le fatture dei docenti verso CEN Engineering'
              : 'Visualizza le tue fatture e i tuoi acquisti (chiavi e abbonamenti)'}
          </p>
        </div>
        {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Fattura
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Carica Fattura</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Docente *</Label>
                <Select value={formData.teacher_id} onValueChange={(v) => setFormData({...formData, teacher_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona docente" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Numero Fattura *</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                    required
                    placeholder="FT-2024-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Importo (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  placeholder="es. Corso sicurezza base - Gennaio 2024"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Emissione *</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Scadenza</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>File Fattura (PDF)</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                />
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
              <Button type="submit" className="w-full" disabled={uploading || !formData.teacher_id}>
                {uploading ? 'Caricamento...' : 'Carica Fattura'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Fatture</p>
                <p className="text-2xl font-bold">{invoices.length}</p>
              </div>
              <FileSpreadsheet className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Da Pagare</p>
                <p className="text-2xl font-bold text-amber-600">€ {totalPending.toFixed(2)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pagate</p>
                <p className="text-2xl font-bold text-green-600">€ {totalPaid.toFixed(2)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        {isAdmin && (
        <Select value={filterTeacher} onValueChange={setFilterTeacher}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtra per docente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i docenti</SelectItem>
            {teachers.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="paid">Pagate</SelectItem>
            <SelectItem value="overdue">Scadute</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nessuna fattura</h3>
            <p className="text-muted-foreground">
              {invoices.length > 0 ? 'Nessuna fattura corrisponde ai filtri' : 'Carica la prima fattura per iniziare'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice, index) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{invoice.invoice_number}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[invoice.status]}`}>
                            {statusLabels[invoice.status]}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{invoice.is_stripe_purchase ? 'CEN Engineering (acquisto Stripe)' : getTeacherName(invoice.teacher_id)}</p>
                        {invoice.description && (
                          <p className="text-sm text-muted-foreground mt-1">{invoice.description}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-2xl font-bold">€ {(invoice.amount || 0).toFixed(2)}</p>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {new Date(invoice.issue_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {invoice.file_url && (
                          <a href={`${invoice.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Apri
                            </Button>
                          </a>
                        )}
                        {isAdmin && !invoice.is_stripe_purchase && invoice.status === 'pending' && (
                          <Button size="sm" onClick={() => handleMarkPaid(invoice.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Segna Pagata
                          </Button>
                        )}
                        {isAdmin && !invoice.is_stripe_purchase && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
